/* CRA's ESLint loader does not ship the react-hooks/set-state-in-effect rule */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, ThumbsUp, ThumbsDown, HelpCircle } from "lucide-react";
import api from "@/lib/api";

const ICON = {
  yes: { Icon: ThumbsUp, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  maybe: { Icon: HelpCircle, color: "text-amber-700 bg-amber-50 border-amber-200" },
  no: { Icon: ThumbsDown, color: "text-red-700 bg-red-50 border-red-200" },
};

const LABEL = { yes: "Yes", maybe: "Maybe", no: "No" };

function relativeTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function HiringManagerResponses({ jobIdMap = {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard/brief-responses?limit=6")
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], last_30d: { yes: 0, maybe: 0, no: 0 } }))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div
        data-testid="hm-responses-card"
        className="border border-slate-200 p-5"
      >
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          <Inbox className="h-3 w-3" /> Hiring-manager responses
        </div>
        <p className="mt-4 text-sm text-slate-500">Loading…</p>
      </div>
    );
  }

  const { items, last_30d } = data;
  const total = last_30d.yes + last_30d.maybe + last_30d.no;

  return (
    <div
      data-testid="hm-responses-card"
      className="border border-slate-200 p-5"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          <Inbox className="h-3 w-3" /> Hiring-manager responses
        </div>
        {total > 0 && (
          <div className="flex items-center gap-3 text-[11px] text-slate-600">
            <span className="font-medium text-emerald-700">
              👍 {last_30d.yes}
            </span>
            <span className="font-medium text-amber-700">
              🤔 {last_30d.maybe}
            </span>
            <span className="font-medium text-red-700">
              👎 {last_30d.no}
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
              30d
            </span>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <p
          data-testid="hm-responses-empty"
          className="mt-4 text-sm leading-relaxed text-slate-500"
        >
          No verdicts yet. After you send a brief from the Review page, hiring
          managers can submit 👍/👎/maybe right inside Hireloop — responses
          land here.
        </p>
      ) : (
        <ul className="mt-4 space-y-3" data-testid="hm-responses-list">
          {items.map((it) => {
            const cfg = ICON[it.decision] || ICON.maybe;
            const Icon = cfg.Icon;
            const jobId = jobIdMap[it.interview_id];
            const linkTo = jobId
              ? `/jobs/${jobId}/candidates/${it.interview_id}`
              : `/dashboard`;
            return (
              <li
                key={it.id}
                data-testid={`hm-response-item-${it.id}`}
                data-decision={it.decision}
                className="border border-slate-200 p-3"
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center border ${cfg.color}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900">
                      {LABEL[it.decision]} ·{" "}
                      <Link
                        to={linkTo}
                        data-testid={`hm-response-link-${it.id}`}
                        className="text-blue-700 hover:underline"
                      >
                        {it.candidate_name}
                      </Link>
                    </p>
                    <p className="text-xs text-slate-500">
                      For <strong>{it.job_title}</strong> · from{" "}
                      {it.hiring_manager_name || it.hiring_manager_email} ·{" "}
                      {relativeTime(it.responded_at)}
                    </p>
                    {it.comment && (
                      <p className="mt-2 border-l-2 border-slate-300 pl-2 text-xs italic text-slate-700">
                        “{it.comment}”
                      </p>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
